import {useEffect} from 'react';
import {useAuthUser} from '../auth/hooks/useAuthUser';
import {useSigninStatus} from '../auth/hooks/useSigninStatus';
import {FullScreenLoading} from '../common/Loading';
import {startupProfiler} from '../common/utils/startupProfiler';
import {ComponentWithRequiredChildren, PropsWithRequiredChildren} from '../react/types';
import {useGetOrCreateCurrentUser} from './hooks/useGetOrCreateCurrentUser';
import {CurrentUserContext} from './hooks/useOptionalCurrentUser';

export const WithCurrentUser: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const {isLoading, isSignedIn, signinError} = useSigninStatus();

  useEffect(() => {
    if (!isLoading) {
      startupProfiler.mark(`Auth Signin Status Resolved: ${isSignedIn ? 'Signed In' : 'Signed Out'}`);
    }
  }, [isLoading, isSignedIn]);

  if (signinError) {
    throw signinError;
  } else if (isLoading) {
    return <FullScreenLoading />;
  } else if (isSignedIn) {
    return <WaitForAuth>{children}</WaitForAuth>;
  }

  return <>{children}</>;
};

const WaitForAuth: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const {error, status} = useAuthUser();

  useEffect(() => {
    if (status !== 'loading') {
      startupProfiler.mark(`Auth User Token Resolved (${status})`);
    }
  }, [status]);

  if (error) {
    throw error;
  } else if (status === 'loading') {
    return <FullScreenLoading />;
  }
  return <WaitForUser>{children}</WaitForUser>;
};

const WaitForUser: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const user = useGetOrCreateCurrentUser();
  const isLoaded = !user.isLoading() && !!user.get();

  useEffect(() => {
    if (isLoaded) {
      startupProfiler.mark('Current User Document Loaded');
    }
  }, [isLoaded]);

  if (user.isLoading() || !user.get()) {
    return <FullScreenLoading />;
  }
  return <CurrentUserContext value={user}>{children}</CurrentUserContext>;
};
